/*!
 * EGT media inventory — a server-side scan that finds every image/video the EGT
 * surfaces reference, grouped by PAGE -> SECTION, with on-disk existence.
 *
 * This is a READ-ONLY scanner (it never writes). It greps the EGT source — the
 * page elements (app/elements/egt.*.js), the shared section helpers
 * (app/helpers/egt.*.js) and the page JSON (app/pages/*.json) — for public media
 * paths (img/video <src>, poster, and the S.media({img,video,poster}) /
 * egt-canvas__img patterns), then attributes each reference to the page it
 * powers and, where it can be inferred, to a section (the nearest render*()
 * function name or section comment above the reference).
 *
 * Output shape (what the media-manager element + route consume):
 *   {
 *     scannedOn: ISO,
 *     publicRoot: "/abs/app/public",
 *     pages: [ { key, label, slugs:[...], sections:[ { name, items:[ item ] } ] } ],
 *     totals: { items, missing, files }
 *   }
 *   item = {
 *     path: "/imgs/pf/pf-search.jpg",   // the referenced public path
 *     ext: "jpg",
 *     kind: "image" | "video",
 *     exists: true|false,
 *     bytes: <number|null>,
 *     refs: [ { file: "app/elements/egt.platform.reach.js", line: 42, section: "renderReach" } ]
 *   }
 */

var fm = require("../../uam/filemanager.js");
var fs = require("fs");
var path = require("path");

/* the public folder is the first static dir; everything a path like /imgs/x.jpg
 * resolves to lives under here. We resolve via the file manager (app-sandboxed).
 * Guarded (not left to throw): this module is a server-side scanner, but
 * `egt.sections.js` (required by every page element) pulls it in, so webpack
 * also bundles it for the CLIENT, where `fm.resolve` has no real filesystem to
 * resolve against. An unguarded throw here used to kill the ENTIRE app-element
 * client registry for every page (not just this one) — the whole
 * `.includes/uat.registry.client.js` require chain aborts on the first module
 * that throws while loading, so downstream `registry.add()` calls for every
 * app control (egt.select.datetimespan, egt.people.group, etc.) never ran and
 * silently fell back to a UAT-only registry with zero app elements. */
var PUBLIC_ROOT = null;
try { PUBLIC_ROOT = fm.resolve("@!/app/public"); } catch (e) { /* no real FS client-side */ }

var IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "avif", "gif", "svg"];
var VIDEO_EXT = ["mp4", "webm"];
var ALL_EXT = IMAGE_EXT.concat(VIDEO_EXT);

/* Page model — the human-facing pages, the EGT element namespaces / source files
 * that power each, and the route slugs they serve. A source file maps to exactly
 * one page (the shared helpers map to "shared"). The order here is the display
 * order in the surface. */
var PAGES = [
  { key: "home", label: "Home", slugs: ["/"],
    elements: ["egt.home"], files: ["egt.home.js"] },
  { key: "platform", label: "Platform", slugs: ["/platform", "/platform/site", "/platform/marketplace", "/platform/reach", "/platform/engage", "/platform/appartisan", "/platform/workflows", "/platform/compare/the-stack"],
    elements: ["egt.platform", "egt.platform.site", "egt.platform.marketplace", "egt.platform.reach", "egt.platform.engage", "egt.platform.appartisan", "egt.platform.workflows", "egt.platform.compare"],
    files: ["egt.platform.js", "egt.platform.site.js", "egt.platform.marketplace.js", "egt.platform.reach.js", "egt.platform.engage.js", "egt.platform.appartisan.js", "egt.platform.workflows.js", "egt.platform.compare.js", "egt.platform.sections.js"] },
  { key: "intrepid", label: "Intrepid", slugs: ["/intrepid"],
    elements: ["egt.intrepid"], files: ["egt.intrepid.js"] },
  { key: "forge", label: "Forge", slugs: ["/forge", "/forge/start"],
    elements: ["egt.forge", "egt.forge.start"], files: ["egt.forge.js", "egt.forge.start.js"] },
  { key: "studio", label: "Studio", slugs: ["/studio"],
    elements: ["egt.studio"], files: ["egt.studio.js"] },
  { key: "entertainment", label: "Entertainment", slugs: ["/entertainment"],
    elements: ["egt.entertainment"], files: ["egt.entertainment.js"] },
  { key: "about", label: "About", slugs: ["/about"],
    elements: ["egt.about", "egt.people.group"], files: ["egt.about.js", "egt.people.group.js"] },
  { key: "reserve", label: "Reserve", slugs: ["/reserve"],
    elements: ["egt.reserve"], files: ["egt.reserve.js"] },
  { key: "exploit", label: "Exploit", slugs: ["/exploit"],
    elements: ["egt.exploit"], files: ["egt.exploit.js"] },
  { key: "contact", label: "Contact", slugs: ["/contact-us"],
    elements: ["egt.contact", "egt.contact.thanks"], files: ["egt.contact.js", "egt.contact.thanks.js"] },
  /* The in-between /legal landing page + its egt.legal element were removed
   * 2026-07-31 (founder directive); /legal now 301s to /legal/codex. */
  { key: "legal", label: "Legal", slugs: ["/legal/codex", "/legal/baa"],
    elements: ["egt.codex", "egt.baa"], files: ["egt.codex.js", "egt.baa.js"] },
  { key: "notfound", label: "404", slugs: ["/404"],
    elements: ["egt.notfound"], files: ["egt.notfound.js"] },
  { key: "shared", label: "Shared (nav · footer · primitives)", slugs: [],
    elements: [], files: ["egt.sections.js", "egt.markdown.js"] }
];

/* a file may not be listed above (a new egt.* element); it still gets scanned and
 * lands in an "Other surfaces" page so nothing is silently dropped. */
function pageForFile(fileName) {
  for (var i = 0; i < PAGES.length; i++) {
    if (PAGES[i].files.indexOf(fileName) !== -1) { return PAGES[i].key; }
  }
  return "other";
}

var PAGE_BY_KEY = {};
PAGES.forEach(function (p) { PAGE_BY_KEY[p.key] = p; });

function lastSegment(slug) {
  var parts = String(slug).split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

/* bestSlug — the live URL to open so a founder can SEE an asset in context.
 * Single-route surfaces resolve to their one slug. Multi-route surfaces
 * (Platform, Forge, Legal) match the source file's last base segment to a slug's
 * last segment (egt.platform.reach.js -> /platform/reach; egt.legal.codex.js ->
 * /legal/codex), falling back to the surface's primary slug. Shared/other files
 * (no owning route) return "" — no view link. */
function bestSlug(pageKey, fileName) {
  var page = PAGE_BY_KEY[pageKey];
  if (!page || !page.slugs || !page.slugs.length) { return ""; }
  if (page.slugs.length === 1) { return page.slugs[0]; }
  var base = String(fileName).replace(/\.(js|json)$/i, "").replace(/^egt\./i, "");
  var seg = base.split(".").pop();
  for (var i = 0; i < page.slugs.length; i++) {
    if (lastSegment(page.slugs[i]) === seg) { return page.slugs[i]; }
  }
  return page.slugs[0];
}

function extOf(p) {
  var m = String(p).toLowerCase().match(/\.([a-z0-9]+)(?:[?#].*)?$/);
  return m ? m[1] : "";
}
function kindOf(ext) { return VIDEO_EXT.indexOf(ext) !== -1 ? "video" : "image"; }

/* The reference matcher. We want app-LOCAL public paths only (start with "/",
 * not "//" and not a scheme). We capture any quoted string whose value ends in a
 * known media extension, regardless of the property name, so we catch
 * src/poster/img/video/icon/art and any future ones. */
var REF_RE = new RegExp(
  "[\"']" +                        // opening quote
  "(\\/(?!\\/)[^\"'\\s)]*?\\." +    // a root-relative path "/...."
  "(?:" + ALL_EXT.join("|") + "))" +
  "(?:[?#][^\"']*)?" +             // optional query/hash
  "[\"']",                         // closing quote
  "gi"
);

/* infer the section for a line: the nearest preceding `function renderX(` /
 * `function X(` definition, else the nearest `/* SECTION ... *​/` or top comment
 * token, else "page". Operates on the file's lines up to the match line. */
function inferSection(lines, lineIdx) {
  for (var i = lineIdx; i >= 0; i--) {
    var fn = lines[i].match(/function\s+(render[A-Za-z0-9_]+|[A-Za-z0-9_]*[Ss]ection[A-Za-z0-9_]*)\s*\(/);
    if (fn) { return fn[1]; }
  }
  /* fall back to an ALL-CAPS section banner comment (e.g. "/* HERO — ...") */
  for (var j = lineIdx; j >= 0; j--) {
    var cm = lines[j].match(/^\s*\/\*+\s*([A-Z][A-Z0-9 _\-]{2,30})\b/);
    if (cm) { return cm[1].trim().toLowerCase().replace(/\s+/g, "-"); }
  }
  return "page";
}

/* scan one source file, returning [ { path, ext, kind, file, line, section } ] */
function scanFile(absPath, relName) {
  var text;
  try { text = fs.readFileSync(absPath, "utf8"); }
  catch (e) { return []; }
  var lines = text.split(/\r?\n/);
  var out = [];
  for (var li = 0; li < lines.length; li++) {
    var line = lines[li];
    REF_RE.lastIndex = 0;
    var m;
    while ((m = REF_RE.exec(line)) !== null) {
      var p = m[1];
      var ext = extOf(p);
      if (ALL_EXT.indexOf(ext) === -1) { continue; }
      out.push({
        path: p, ext: ext, kind: kindOf(ext),
        file: relName, line: li + 1,
        section: relName.indexOf(".json") !== -1 ? "page meta" : inferSection(lines, li)
      });
    }
  }
  return out;
}

/* check a public path exists on disk under app/public (no traversal). Returns
 * { exists, bytes }. A path that resolves outside app/public is treated as
 * missing (it should never happen for a "/..." path, but fail closed). */
function diskStat(publicPath) {
  var rel = publicPath.replace(/^\//, "").replace(/[?#].*$/, "");
  var abs = path.resolve(PUBLIC_ROOT, rel);
  if (abs !== PUBLIC_ROOT && abs.indexOf(PUBLIC_ROOT + path.sep) !== 0) {
    return { exists: false, bytes: null };
  }
  try {
    var st = fs.statSync(abs);
    return { exists: st.isFile(), bytes: st.isFile() ? st.size : null };
  } catch (e) { return { exists: false, bytes: null }; }
}

/* gather every egt.* source file + the page JSON files to scan. */
function sourceFiles() {
  var files = [];
  var elementsDir = fm.resolve("@!/app/elements");
  var helpersDir = fm.resolve("@!/app/helpers");
  var pagesDir = fm.resolve("@!/app/pages");
  function pushFrom(dir, relPrefix, filterFn) {
    var names;
    try { names = fs.readdirSync(dir); } catch (e) { return; }
    names.forEach(function (n) {
      if (!filterFn(n)) { return; }
      files.push({ abs: path.join(dir, n), rel: relPrefix + n, name: n });
    });
  }
  /* exclude the media-manager's OWN source so it never inventories itself
   * (its PAGES model + UI strings contain sample paths that aren't real refs). */
  var SELF = { "egt.media-inventory.js": 1, "egt.media-manager.js": 1 };
  pushFrom(elementsDir, "app/elements/", function (n) { return /^egt\..*\.js$/.test(n) && !SELF[n]; });
  pushFrom(helpersDir, "app/helpers/", function (n) { return /^egt\..*\.js$/.test(n) && !SELF[n]; });
  pushFrom(pagesDir, "app/pages/", function (n) { return /\.json$/.test(n); });
  return files;
}

/* Directories under app/public that hold real media, and the page each maps to.
 * Some surfaces (notably Exploit) load their stills dynamically by slug, so those
 * files are never quoted as literal paths and the reference scan can't see them.
 * A disk-scan of these dirs guarantees EVERY photo/video shows up; anything not
 * already referenced lands in an "On disk" section on its page. */
var DISK_DIRS = [
  { rel: "imgs/exploit", page: "exploit" }
];

/* every media file physically present in the DISK_DIRS, as public "/..." paths. */
function diskMediaFiles() {
  var out = [];
  DISK_DIRS.forEach(function (d) {
    var abs;
    try { abs = fm.resolve("@!/app/public/" + d.rel); } catch (e) { return; }
    var names;
    try { names = fs.readdirSync(abs); } catch (e2) { return; }
    names.forEach(function (n) {
      var ext = extOf(n);
      if (ALL_EXT.indexOf(ext) === -1) { return; }         // media files only
      out.push({ path: "/" + d.rel + "/" + n, ext: ext, kind: kindOf(ext), page: d.page });
    });
  });
  return out;
}

/* page JSON files attribute to a page by their slug/name; map filename -> page key. */
var JSON_PAGE = {
  "index.json": "home", "platform.json": "platform", "intrepid.json": "intrepid",
  "forge.json": "forge", "studio.json": "studio", "entertainment.json": "entertainment",
  "about.json": "about", "reserve.json": "reserve", "exploit.json": "exploit", "contact-us.json": "contact",
  "contact-us-thanks.json": "contact", "404.json": "notfound"
};

/**
 * Builds the full media inventory.
 * @returns {object} the inventory (see file header for shape).
 */
function build() {
  var byPath = {};   // path -> item (deduped across files; refs accumulate)
  var files = sourceFiles();

  files.forEach(function (f) {
    var refs = scanFile(f.abs, f.rel);
    refs.forEach(function (r) {
      var key;
      if (f.name.indexOf(".json") !== -1) {
        key = JSON_PAGE[f.name] || "other";
      } else {
        key = pageForFile(f.name);
      }
      var item = byPath[r.path];
      if (!item) {
        var d = diskStat(r.path);
        item = {
          path: r.path, ext: r.ext, kind: r.kind,
          exists: d.exists, bytes: d.bytes,
          poster: r.kind === "video" ? posterFor(r.path) : null,
          pages: {}, refs: []
        };
        byPath[r.path] = item;
      }
      item.pages[key] = true;
      item.refs.push({ file: r.file, line: r.line, section: r.section, page: key, slug: bestSlug(key, f.name) });
    });
  });

  /* fold in on-disk media that the reference scan can't see (dynamic-by-slug
   * loads, e.g. the Exploit gallery + soul stills). Anything already referenced
   * keeps its real refs; the rest surface under an "On disk" section on its page
   * so every photo/video is visible and replaceable. */
  diskMediaFiles().forEach(function (m) {
    if (byPath[m.path]) { return; }              // already referenced — leave it
    var d = diskStat(m.path);
    var item = {
      path: m.path, ext: m.ext, kind: m.kind,
      exists: d.exists, bytes: d.bytes,
      poster: m.kind === "video" ? posterFor(m.path) : null,
      pages: {}, refs: []
    };
    item.pages[m.page] = true;
    item.refs.push({ file: "(on disk)", line: 0, section: "on-disk", page: m.page,
      slug: bestSlug(m.page, "") });
    byPath[m.path] = item;
  });

  /* assemble the page -> section -> items tree. An item appears under EACH page
   * that references it (shared assets like /logo.svg show under their real users
   * AND under Shared). Within a page, items group by the section of their first
   * ref on that page. */
  var pageMap = {};
  PAGES.forEach(function (p) { pageMap[p.key] = { key: p.key, label: p.label, slugs: p.slugs, sectionMap: {} }; });
  pageMap.other = { key: "other", label: "Other surfaces", slugs: [], sectionMap: {} };

  var totals = { items: 0, missing: 0, files: 0 };
  var seenFiles = {};

  Object.keys(byPath).forEach(function (pth) {
    var item = byPath[pth];
    totals.items++;
    if (!item.exists) { totals.missing++; }

    Object.keys(item.pages).forEach(function (pageKey) {
      var pg = pageMap[pageKey] || pageMap.other;
      /* the section under THIS page = first ref on this page */
      var ref = item.refs.filter(function (x) { return x.page === pageKey; })[0] || item.refs[0];
      var secName = ref.section || "page";
      if (!pg.sectionMap[secName]) { pg.sectionMap[secName] = []; }
      pg.sectionMap[secName].push(item);
    });

    item.refs.forEach(function (r) { seenFiles[r.file] = true; });
  });
  totals.files = Object.keys(seenFiles).length;

  /* materialize ordered arrays */
  var pages = [];
  var order = PAGES.map(function (p) { return p.key; }).concat(["other"]);
  order.forEach(function (k) {
    var pg = pageMap[k];
    if (!pg) { return; }
    var sections = Object.keys(pg.sectionMap).sort().map(function (sName) {
      var items = pg.sectionMap[sName].slice().sort(function (a, b) {
        return a.path < b.path ? -1 : a.path > b.path ? 1 : 0;
      });
      return { name: sName, items: items };
    });
    if (sections.length === 0) { return; } // hide empty pages
    pages.push({ key: pg.key, label: pg.label, slugs: pg.slugs, sections: sections });
  });

  return {
    scannedOn: new Date().toISOString(),
    publicRoot: PUBLIC_ROOT,
    allowedExtensions: ALL_EXT,
    pages: pages,
    totals: totals
  };
}

/* poster sibling convention, shared by the media-manager route (which writes
 * posters) and any page element (which reads them to auto-set <video poster>).
 * "/vis/room.mp4" -> "/vis/room.poster.jpg". A fixed suffix WE append — never
 * derived from user input — so it can't escape the video's own directory. */
function posterAbsFor(videoAbs) { return String(videoAbs).replace(/\.[^.]+$/, ".poster.jpg"); }

/* posterFor — given a public video path ("/vis/room.mp4"), return its poster's
 * public path IF one has been captured/uploaded on disk, else null. Used at
 * render time so hero/section videos automatically pick up a poster once one
 * exists, with no per-video wiring. Fails closed (path must resolve inside
 * app/public) and never throws. */
function posterFor(publicVideoPath) {
  if (typeof publicVideoPath !== "string" || !publicVideoPath) { return null; }
  var rel = publicVideoPath.replace(/^\/+/, "").replace(/[?#].*$/, "");
  var abs = path.resolve(PUBLIC_ROOT, rel);
  if (abs !== PUBLIC_ROOT && abs.indexOf(PUBLIC_ROOT + path.sep) !== 0) { return null; }
  var posterAbs = posterAbsFor(abs);
  try {
    if (fs.statSync(posterAbs).isFile()) {
      return "/" + path.relative(PUBLIC_ROOT, posterAbs).split(path.sep).join("/");
    }
  } catch (e) { /* no poster captured yet */ }
  return null;
}

module.exports = {
  build: build,
  diskStat: diskStat,
  PUBLIC_ROOT: PUBLIC_ROOT,
  IMAGE_EXT: IMAGE_EXT,
  VIDEO_EXT: VIDEO_EXT,
  ALL_EXT: ALL_EXT,
  posterAbsFor: posterAbsFor,
  posterFor: posterFor
};
